import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function LoadingSpinner({ size = 'md', className }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  return (
    <Loader2
      className={cn(
        'animate-spin text-primary-600',
        sizeClasses[size],
        className
      )}
      aria-hidden="true"
    />
  );
}

export function LoadingPage() {
  return (
    <div className="min-h-screen flex items-center justify-center" role="status" aria-label="Ladataan sisältöä">
      <div className="text-center">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-500">Ladataan...</p>
      </div>
    </div>
  );
}

export function LoadingCard() {
  return (
    <div className="card p-6 flex items-center justify-center" role="status" aria-label="Ladataan">
      <LoadingSpinner />
    </div>
  );
}

// Skeleton components for loading states
interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn('skeleton-shimmer rounded-sm', className)}
      aria-hidden="true"
    />
  );
}

// Workout card skeleton
export function WorkoutCardSkeleton() {
  return (
    <div className="card p-4 animate-fade-in" aria-hidden="true">
      <div className="flex items-start gap-4">
        <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
        <Skeleton className="w-8 h-8 rounded-lg" />
      </div>
    </div>
  );
}

// List of workout skeletons
export function WorkoutListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-3" role="status" aria-label="Ladataan treenejä">
      {Array.from({ length: count }).map((_, i) => (
        <WorkoutCardSkeleton key={i} />
      ))}
      <span className="sr-only">Ladataan treenejä...</span>
    </div>
  );
}

// Exercise row skeleton
export function ExerciseRowSkeleton() {
  return (
    <div className="flex items-center gap-3 px-3 py-3" aria-hidden="true">
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
      </div>
      <Skeleton className="w-5 h-5" />
    </div>
  );
}

// Exercise list skeleton
export function ExerciseListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-1" role="status" aria-label="Ladataan liikkeitä">
      {Array.from({ length: count }).map((_, i) => (
        <ExerciseRowSkeleton key={i} />
      ))}
      <span className="sr-only">Ladataan liikkeitä...</span>
    </div>
  );
}

// Stats card skeleton
export function StatsCardSkeleton() {
  return (
    <div className="card p-4 space-y-3 animate-fade-in" aria-hidden="true">
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-32 w-full rounded-lg" />
    </div>
  );
}

// Button with loading state
interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  loadingText?: string;
  children: React.ReactNode;
}

export function LoadingButton({
  isLoading = false,
  loadingText = 'Ladataan...',
  children,
  disabled,
  className,
  ...props
}: LoadingButtonProps) {
  return (
    <button
      disabled={isLoading || disabled}
      className={className}
      aria-busy={isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <LoadingSpinner size="sm" className="mr-2" />
          <span>{loadingText}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}
